import React from 'react';
import {
  CardVariant,
  SizeVariant,
  BaseComponentProps,
  ClickableProps,
  WithChildrenProps,
} from '../types/componentThemes';

export interface CardProps
  extends BaseComponentProps,
    ClickableProps,
    Omit<WithChildrenProps, 'children'> {
  /** Card style variant */
  variant?: CardVariant;
  /** Padding size */
  padding?: SizeVariant;
  /** Optional header content */
  header?: React.ReactNode;
  /** Optional footer content */
  footer?: React.ReactNode;
  /** Main card content */
  children?: React.ReactNode;
}

export const Card: React.FC<CardProps> = ({
  variant = 'default',
  padding = 'md',
  header,
  footer,
  children,
  className = '',
  style,
  testId,
  onClick,
}) => {
  const baseClasses = [
    'bg-white',
    'dark:bg-neutral-800',
    'rounded-xl',
    'overflow-hidden',
    'transition-all',
    'duration-200',
    'border',
    'border-neutral-200/60',
    'dark:border-neutral-700/60',
  ];

  const variantClasses: Record<CardVariant, string[]> = {
    default: ['shadow-sm'],
    elevated: ['shadow-md'],
    bordered: ['border-neutral-200', 'dark:border-neutral-700', 'shadow-none'],
    interactive: [
      'shadow-sm',
      'cursor-pointer',
      'hover:shadow-md',
      'hover:border-primary-200',
      'dark:hover:border-primary-800',
      'active:scale-[0.99]',
    ],
  };

  const paddingClasses: Record<SizeVariant, string> = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  const cardClasses = [
    ...baseClasses,
    ...variantClasses[variant],
    className,
  ].join(' ');

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onClick && variant === 'interactive') {
      onClick();
    }
  };

  const isClickable = variant === 'interactive' && onClick;

  return (
    <div
      className={cardClasses}
      style={style}
      data-testid={testId}
      onClick={isClickable ? handleClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      {header && (
        <div
          className={`${paddingClasses[padding]} border-b border-neutral-100 dark:border-neutral-700/60`}
          data-testid={testId ? `${testId}-header` : undefined}
        >
          {header}
        </div>
      )}

      {children && (
        <div
          className={paddingClasses[padding]}
          data-testid={testId ? `${testId}-body` : undefined}
        >
          {children}
        </div>
      )}

      {footer && (
        <div
          className={`${paddingClasses[padding]} border-t border-neutral-100 dark:border-neutral-700/60 bg-neutral-50/50 dark:bg-neutral-800/50`}
          data-testid={testId ? `${testId}-footer` : undefined}
        >
          {footer}
        </div>
      )}
    </div>
  );
};
